/**
 * PlaygroundNavbar.tsx
 * Persistent navigation bar for the playground
 * 
 * Provides top-level navigation between playground sections:
 * Dashboard | Components | Themes | Data
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  NavbarMenuToggle,
  NavbarMenu,
  NavbarMenuItem
} from '@enfomentis/ui';
import { Link } from '@enfomentis/ui';
import { ButtonGroup } from '@enfomentis/ui';
import { BrandMark, BrandText } from '@enfomentis/ui';
import type { PlaygroundTheme } from './usePlaygroundTheme';

interface PlaygroundNavbarProps {
  currentTheme: PlaygroundTheme;
  onThemeChange: (theme: string) => void;
}

/**
 * Navigation items configuration
 */
const navigationItems = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/playground',
    description: 'Main playground overview'
  },
  {
    id: 'components',
    label: 'Components',
    path: '/playground/components',
    description: 'UI component examples'
  },
  {
    id: 'themes',
    label: 'Themes',
    path: '/playground/themes',
    description: 'Theme system playground'
  },
  {
    id: 'data',
    label: 'Data',
    path: '/playground/data',
    description: 'Data visualization examples'
  }
];

/**
 * Persistent Playground Navigation Bar
 * 
 * Features:
 * - Top-level section navigation (Dashboard, Components, Themes, Data)
 * - Active state highlighting for current section
 * - Theme toggle integrated
 * - Mobile responsive with collapsible menu
 * - Professional branding
 */
export const PlaygroundNavbar: React.FC<PlaygroundNavbarProps> = ({
  currentTheme = 'light',
  onThemeChange
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  // Determine active section based on current path
  const getActiveSection = () => {
    const path = location.pathname;
    
    if (path === '/playground') return 'dashboard';
    if (path.startsWith('/playground/components')) return 'components';
    if (path.startsWith('/playground/themes')) return 'themes';
    if (path.startsWith('/playground/data')) return 'data';
    
    return '';
  };

  const activeSection = getActiveSection();

  // Theme toggle options
  const themeOptions = [
    { label: 'Light', value: 'light' },
    { label: 'Dark', value: 'dark' },
    { label: 'Auto', value: 'auto' }
  ];

  // Handle navigation
  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMenuOpen(false); // Close mobile menu after navigation
  };

  return (
    <Navbar 
      isBordered
      isMenuOpen={isMenuOpen}
      onMenuOpenChange={setIsMenuOpen}
      maxWidth="full"
      className="bg-background border-border"
    >
      {/* Brand and Mobile Toggle */}
      <NavbarContent>
        <NavbarMenuToggle
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          className="sm:hidden"
        />
        <NavbarBrand className="cursor-pointer" onClick={() => handleNavigation('/playground')}>
          <BrandMark size={28} className="mr-2 -mt-1" />
          <div className="flex flex-col">
            <BrandText height={16} className="text-text-primary" />
            <span className="text-xs text-text-secondary">Playground</span>
          </div>
        </NavbarBrand>
      </NavbarContent>

      {/* Desktop Navigation Items */}
      <NavbarContent className="hidden sm:flex gap-4" justify="center">
        {navigationItems.map((item) => (
          <NavbarItem key={item.id} isActive={activeSection === item.id}>
            <Link
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleNavigation(item.path);
              }}
              color={activeSection === item.id ? 'primary' : 'foreground'}
              className="font-medium"
            >
              {item.label}
            </Link>
          </NavbarItem>
        ))}
      </NavbarContent>

      {/* Theme Toggle */}
      <NavbarContent justify="end" className="shrink-0">
        <NavbarItem className="hidden sm:flex">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-text-primary">Theme:</span>
            <ButtonGroup
              options={themeOptions}
              value={currentTheme}
              onChange={(value) => onThemeChange?.(String(value))}
              variant="flat"
              size="sm"
            />
          </div>
        </NavbarItem>
      </NavbarContent>

      {/* Mobile Menu */}
      <NavbarMenu>
        {navigationItems.map((item) => (
          <NavbarMenuItem key={item.id}>
            <Link
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleNavigation(item.path);
                setIsMenuOpen(false);
              }}
              color={activeSection === item.id ? 'primary' : 'foreground'}
              size="lg"
              className="w-full"
            >
              {item.label}
            </Link>
          </NavbarMenuItem>
        ))}
        
        {/* Mobile Theme Toggle */}
        <NavbarMenuItem>
          <div className="flex flex-col gap-2 w-full pt-4">
            <span className="text-sm font-medium text-text-primary">Theme:</span>
            <ButtonGroup
              options={themeOptions}
              value={currentTheme}
              onChange={(value) => onThemeChange?.(String(value))}
              variant="flat"
              size="sm"
              className="w-full"
            />
          </div>
        </NavbarMenuItem>
      </NavbarMenu>
    </Navbar>
  );
};